/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox";
import ArgosErrorMessage from "../../../../../../components/ErrorMessage/ArgosErrorMessage";
import Loader from "../../../../../../components/Loader/Loader";
import {RightPanelPlot} from "./RightPanelPlot";
import {useHasWriteAccess} from "../../../../../../../redux/ducks/AccessControl/accessControlHelpers";
import ArgosSelect from "../../../../../../components/Form/Select/ArgosSelect";
import {InputLabel} from "@mui/material";
import React, {useEffect, useState} from "react";
import ArgosMenuItem from "../../../../../../components/Form/Select/ArgosMenuItem";
import {usePostRequest} from "../../../../../../../api/helper";
import {Nullable} from "../../../../../../../utils/globalTypeUtils";
import {getRadarLandingEndpoint} from "../../../constants";

interface IRightPanelProps {
  isLoadingPlot: boolean
  isError: boolean
  plotData: any
  handleUpdatePlot: any
  baselineData: any
  handleReset: any
  handleClickSaveOpen: any
  openSavePopup: any
  handleClose: any
  handleSaveConfigName: any
  configName: string
  configIndex: any
  gmid: any
  onOverrideConfiguration: any
  onSaveConfiguration: any
  onDeleteConfiguration: any
  fovAndBoresightRadarValues: any

  plotDataMap: Record<string, any>
  fovAndBoresightRadarValuesMap: Record<string, any>

  selectedSensorIds: string[]
  setSelectedSensorIds: (ids: string[]) => void
}

interface IRadar {
  id: string;
  version: string;
  supplier: string;
  component: string;
}

export const getRadarDisplayName = (radar: IRadar) => {
  return `${radar.supplier} ${radar.component} V${radar.version}`;
}

const RightPanel = (props: IRightPanelProps) => {
  const handleFetchAllRadars = usePostRequest(getRadarLandingEndpoint);
  const [allRadars, setAllRadars] = useState<Nullable<Record<string, IRadar>>>(null);
  const {selectedSensorIds, setSelectedSensorIds} = props


  useEffect(() => {
    const payload = {
      'onlyRadarWithData': true
    }
    handleFetchAllRadars(payload)
      .then((response: any) => {
        if (response?.sensors) {
          const radarMap = response.sensors.reduce(
            (acc: Record<string, IRadar>, radar: IRadar) => {
              acc[radar.id] = radar;
              return acc;
            },
            {}
          );
          setAllRadars(radarMap);
        }
      })
      .catch((error: any) => {
        console.error("Error fetching radars:", error);
      });
  }, []);

  // Sort radars alphabetically by display name
  const sortedRadars = allRadars
    ? Object.values(allRadars).sort((a, b) =>
      getRadarDisplayName(a).localeCompare(getRadarDisplayName(b))
    )
    : [];


  console.log('props.fovAndBoresightRadarValuesMap', props.fovAndBoresightRadarValuesMap);
  console.log('selectedSensorIds', selectedSensorIds);

  return (
    <ArgosFlexbox style={{overflow: "auto", width: "100%"}} vertical>
      <Loader isLoading={props?.isLoadingPlot}>
        {props?.isError ? (
          <ArgosErrorMessage/>
        ) : (
          <ArgosFlexbox vertical style={{width: "100%"}}>
            <div style={{padding: '0px 5px', width: '100%'}}>
              <InputLabel id="version-select-label">Select sensors to compare</InputLabel>
              <ArgosSelect
                id="version-select"
                labelId="version-select-label"
                inputProps={{"data-testid": "version-select"}}
                fullWidth={true}
                required
                multiple
                disabled={false}
                error={false}
                value={selectedSensorIds}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedSensorIds(typeof value === "string" ? value.split(",") as string[] : value as string[]);
                }}
              >
                {sortedRadars.map((radar: IRadar) => (
                  <ArgosMenuItem key={radar.id} value={radar.id}>
                    {getRadarDisplayName(radar)}
                  </ArgosMenuItem>
                ))}
              </ArgosSelect>
            </div>
            <RightPanelPlot
              data={props?.plotData}
              plotDataMap={props.plotDataMap}
              selectedSensorIds={selectedSensorIds}
              fovAndBoresightRadarValuesMap={props.fovAndBoresightRadarValuesMap}
              fovAndBoresightRadarValues={props?.fovAndBoresightRadarValues}
              allRadars={allRadars}
            />
          </ArgosFlexbox>
        )}
      </Loader>
    </ArgosFlexbox>
  );
};

export default RightPanel;